/**
 * apps/command/src/screens/RemediationScreen.tsx — §4.8's «Tizim bugun nimani
 * tuzatdi».
 *
 * ============================================================================
 * IT SHOWS FAILURES BESIDE SUCCESSES, AND THAT IS THE WHOLE SCREEN.
 * ============================================================================
 *
 * §4.8: a system that "shows its work reads as competence"; one that "silently
 * self-modifies reads as a black box". A screen of green ticks would be the
 * second thing wearing the first thing's clothes — an operator reading six
 * successes cannot tell a healthy fleet from a playbook that has been failing
 * all morning.
 *
 * So every row carries its real status, the halted kiosks get their own block
 * at the top, and the kill switch's state is on the page — because "nothing
 * happened today" means three completely different things depending on whether
 * the feature is on, idle, or given up.
 *
 * There is no "run it now" button. The worker acts from conditions; a control
 * that made it act on demand would be an operator's command wearing
 * automation's attribution, and the log would stop meaning what it says.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { Button, EmptyState, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { BarRow } from '../ui/charts.jsx';

type MbtRemediation = components['schemas']['MbtRemediation'];

interface Halted {
  device_id: string;
  short_code: string;
  mahalla_name: string;
  detail?: Record<string, unknown>;
}

function label(prefix: string, key: string): string {
  return hasKey(`${prefix}.${key}`) ? t(`${prefix}.${key}`) : key;
}

export function RemediationScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const mayToggle = allows('mbt.command.issue');

  const [items, setItems] = useState<MbtRemediation[] | null>(null);
  const [halted, setHalted] = useState<Halted[]>([]);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [changedBy, setChangedBy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/remediation'));
      setItems(data.items as MbtRemediation[]);
      setHalted(data.halted as Halted[]);
      setEnabled(data.enabled);
      const settings = unwrap(await api().GET('/mbt/settings'));
      setChangedBy(settings.updated_by_name ?? null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('error.network'));
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  async function toggle(next: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api().PATCH('/mbt/settings', { body: { auto_remediation_enabled: next } });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('remediation.toggleFailed'));
    } finally {
      setBusy(false);
    }
  }

  const actions: MbtRemediation[] = items ?? [];
  const acked = actions.filter((row) => row.status === 'acked').length;
  const failed = actions.filter((row) => row.status === 'failed' || row.status === 'expired').length;
  const deferred = actions.filter((row) => row.status === 'deferred').length;
  /**
   * Which rule provoked the playbook, as a chart.
   *
   * `triggered_by_rule` is the "why" on every remediation row and no screen has
   * ever aggregated it. A list says the system restarted six kiosks; this says
   * they were all `disk_low`, which is the difference between "the playbooks
   * are working" and "something is filling every disk in the province".
   */
  const byRule = (() => {
    const counts = new Map<string, number>();
    for (const row of actions) {
      const rule = row.triggered_by_rule ?? '—';
      counts.set(rule, (counts.get(rule) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([rule, count]) => ({
        key: rule,
        label: hasKey(`alert.rule.${rule}`) ? t(`alert.rule.${rule}`) : rule,
        value: count,
      }));
  })();

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('remediation.eyebrow')}</span>}
        title={t('nav.remediation')}
        subtitle={t('remediation.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {actions.length}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('remediation.kpiActions')}
          value={String(actions.length)}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={t('remediation.kpiWindow')}
          footerValue={t('remediation.kpiWindowValue')}
        />
        <KpiCard
          label={t('remediation.kpiAcked')}
          /* The success rate, computed against actions that REACHED a kiosk.
             Counting deferred ones as failures would penalise the appeal lock
             for doing its job. */
          value={
            actions.length === 0 ? null : `${Math.round((acked / actions.length) * 100)}%`
          }
          absent={t('app.notYet')}
          tone={failed > 0 ? 'warn' : 'ok'}
          footerLabel={t('remediation.kpiFailed')}
          footerValue={String(failed)}
        />
        <KpiCard
          label={t('remediation.kpiDeferred')}
          value={String(deferred)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('remediation.kpiDeferredMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('remediation.kpiHalted')}
          /* The circuit breaker. The most important number here: a halted
             machine is one the system has STOPPED trying to fix, so nothing
             happens on it until a person acts. */
          value={String(halted.length)}
          absent={t('app.notYet')}
          tone={halted.length > 0 ? 'crit' : 'ok'}
          footerLabel={t('remediation.kpiHaltedMeaning')}
          footerValue=""
        />
      </div>

      {byRule.length > 0 ? (
        <Panel>
          <PanelHeader title={t('remediation.byRule')} aside={t('remediation.byRuleHint')} />
          {/*
            HORIZONTAL, not vertical. Eight categories at most, and every label
            is a sentence — «Dastur qayta-qayta ishga tushmoqda». A column chart
            gives each label 80px and truncates it to «Dastur qayt…», which
            names nothing. Rotated text would be worse. Rows give the label the
            room it needs and the ranking reads top-down, which is how a list of
            causes is read anyway.
          */}
          <div className="space-y-2 px-4 py-4">
            {byRule.map((row) => (
              <BarRow
                key={row.key}
                label={row.label}
                value={row.value}
                max={byRule[0]?.value ?? 1}
                display={String(row.value)}
              />
            ))}
          </div>
        </Panel>
      ) : null}

      {/* §4.8's kill switch. On the log's own page, because a log of "nothing
          happened" means something completely different depending on it. */}
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4">
          <div>
            <p className="text-label text-ink-100">
              {enabled === null
                ? t('app.loading')
                : enabled
                  ? t('remediation.enabled')
                  : t('remediation.disabled')}
            </p>
            {changedBy ? (
              <p className="text-micro text-ink-500">
                {t('remediation.changedBy', { name: changedBy })}
              </p>
            ) : null}
          </div>
          {mayToggle && enabled !== null ? (
            <Button tone="quiet" onClick={() => void toggle(!enabled)} disabled={busy}>
              {enabled ? t('remediation.toggleOff') : t('remediation.toggleOn')}
            </Button>
          ) : null}
        </div>
      </Panel>

      {/* The kiosks the breaker gave up on, FIRST — §9.1's exception-first
          framing. These are the ones needing a person. */}
      {halted.length > 0 ? (
        <Panel>
          <PanelHeader title={t('remediation.haltedTitle')} aside={String(halted.length)} />
          <div className="px-4 py-3">
            <ul className="divide-y divide-hairline">
              {halted.map((kiosk) => (
                <li key={kiosk.device_id} className="py-2">
                  <p className="text-label">
                    <span className="font-medium tabular-nums text-crit">{kiosk.short_code}</span>{' '}
                    <span className="text-ink-500">{kiosk.mahalla_name}</span>
                  </p>
                  <p className="text-micro text-ink-500">
                    {t('remediation.haltedNote', {
                      n: Number(kiosk.detail?.actions ?? 0),
                    })}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader
          title={t('remediation.title')}
          aside={t('remediation.subtitle')}
        />
        <div className="px-4 py-3">
          {items === null ? (
            <Skeleton className="h-24" />
          ) : items.length === 0 ? (
            <EmptyState message={t('remediation.empty')} />
          ) : (
            <ul className="divide-y divide-hairline">
              {items.map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-label">
                      <span className="font-medium tabular-nums text-ink-100">
                        {item.short_code}
                      </span>{' '}
                      <span className="text-ink-300">{label('command.type', item.type)}</span>
                    </p>
                    <p className="text-micro tabular-nums text-ink-500">
                      {ageLabel(new Date(item.issued_at), now, t)}
                      {item.triggered_by_rule
                        ? ` · ${t('remediation.because', {
                            rule: label('alert.rule', item.triggered_by_rule),
                          })}`
                        : ''}
                    </p>
                  </div>
                  {/* The real status, never a tick. A failed or expired
                      automatic action is the most useful row on this screen. */}
                  <span
                    className={`shrink-0 text-micro ${
                      item.status === 'failed' || item.status === 'expired'
                        ? 'text-crit'
                        : item.status === 'deferred'
                          ? 'text-warn'
                          : item.status === 'acked'
                            ? 'text-ok'
                            : 'text-ink-500'
                    }`}
                  >
                    {label('command.status', item.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-micro text-ink-500">{t('remediation.honestNote')}</p>
        </div>
      </Panel>
    </div>
  );
}
